import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { setNewPassword, clearResetPasswordMessage, clearError } from "../../redux/userSlice";

const ResetPasswordPage = () => {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { loading, error, resetPasswordMessage } = useSelector((state) => state.users);

  // Başarı mesajını dinle ve yönlendir
  useEffect(() => {
    if (resetPasswordMessage) {
      toast.success(resetPasswordMessage);
      dispatch(clearResetPasswordMessage());
      
      setTimeout(() => {
        navigate("/signIn");
      }, 2000);
    }
  }, [resetPasswordMessage, dispatch, navigate]);

  // Hata mesajını dinle
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Component temizliği
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearResetPasswordMessage());
    };
  }, [dispatch]);

  const validateForm = () => {
    if (!password || !confirmPassword) {
      toast.error(t("pleaseCompleteAllFields"));
      return false;
    }

    if (password.length < 8) {
      toast.error(t("passwordMinLength"));
      return false;
    }

    if (password !== confirmPassword) {
      toast.error(t("passwordsDoNotMatch"));
      return false;
    }

    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_.])[A-Za-z\d@$!%*?&_.]{8,17}$/;
    if (!passwordPattern.test(password)) {
      toast.error(t("passwordRequirements"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await dispatch(setNewPassword({ id, token, password })).unwrap();
    } catch (err) {
      console.error("Password reset error:", err);
    }
  };

  if (loading) return <p>{t("loading")}</p>;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="bg-white rounded-[30px] shadow-lg p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-[32px] font-poppins font-bold text-text mb-2">
            {t("setNewPassword")}
          </h1>
          <p className="text-[20px] font-poppins text-text">
            {t("pleaseSetNewPassword")}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Password Input */}
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t("newPassword")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 pr-12 border-2 border-cards-border rounded-tr-[30px] rounded-tl-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20] font-poppins"
            />
            <IconButton
              onClick={() => setShowPassword((prev) => !prev)}
              className="!absolute right-3 top-1/2 -translate-y-1/2"
              edge="end"
              size="small"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>

          {/* Confirm Password Input */}
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder={t("confirmPassword")}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-4 py-3 pr-12 border-2 border-cards-border rounded-bl-[30px] rounded-br-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20] font-poppins"
            />
            <IconButton
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="!absolute right-3 top-1/2 -translate-y-1/2"
              edge="end"
              size="small"
            >
              {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>

          {/* Password Requirements */}
          <div className="text-[12px] text-text font-poppins space-y-1 ml-4">
            <p className="font-medium text-red-500">{t("passwordAllCaps")}:</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full h-[61px] ${
              loading ? "bg-gray-400" : "bg-button-bg hover:bg-button-bg-hover"
            } text-white py-2 rounded-full text-[20px] font-poppins mt-6 transition-colors`}
          >
            {loading ? t("updatingPassword") : t("updatePassword")}
          </button>

          {/* Back to Login */}
          <button
            type="button"
            onClick={() => navigate("/signIn")}
            className="w-full py-2 text-text hover:text-text-dark text-[16px] font-poppins transition-colors"
          >
            {t("backToLogin")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;